import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import EmailGate from "../components/EmailGate";
import {
  getApiBaseUrl,
  getStoredContentSessionId,
} from "../lib/gatedContentTracking";

const markdownComponents = {
  h1: ({ node, ...props }) => (
    <h1
      className="mt-10 mb-4 text-2xl sm:text-[26px] font-semibold tracking-tight text-neutral-900 leading-[1.25] first:mt-0"
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <h2
      className="mt-10 mb-3 text-xl sm:text-[22px] font-semibold tracking-tight text-neutral-900 leading-[1.3] first:mt-0"
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <h3
      className="mt-8 mb-2.5 text-lg font-semibold tracking-tight text-neutral-900 leading-[1.35] first:mt-0"
      {...props}
    />
  ),
  h4: ({ node, ...props }) => (
    <h4
      className="mt-6 mb-2 text-[15px] font-semibold uppercase tracking-[0.08em] text-neutral-700 first:mt-0"
      {...props}
    />
  ),
  h5: ({ node, ...props }) => (
    <h5
      className="mt-5 mb-2 text-[14px] font-semibold uppercase tracking-[0.08em] text-neutral-600 first:mt-0"
      {...props}
    />
  ),
  h6: ({ node, ...props }) => (
    <h6
      className="mt-4 mb-2 text-[13px] font-semibold uppercase tracking-[0.08em] text-neutral-500 first:mt-0"
      {...props}
    />
  ),
  p: ({ node, ...props }) => (
    <p
      className="my-4 text-[15.5px] leading-[1.75] text-neutral-700 first:mt-0 last:mb-0"
      {...props}
    />
  ),
  ul: ({ node, ordered, ...props }) => (
    <ul
      className="my-4 pl-6 list-disc marker:text-neutral-400 space-y-2"
      {...props}
    />
  ),
  ol: ({ node, ordered, ...props }) => (
    <ol
      className="my-4 pl-6 list-decimal marker:text-neutral-500 space-y-2"
      {...props}
    />
  ),
  li: ({ node, ordered, checked, ...props }) => (
    <li
      className="text-[15.5px] leading-[1.7] text-neutral-700 pl-1 [&>p]:my-1 [&>ul]:my-2 [&>ol]:my-2"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-neutral-900" {...props} />
  ),
  em: ({ node, ...props }) => (
    <em className="italic text-neutral-800" {...props} />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-blue-600 underline decoration-blue-200 underline-offset-2 hover:decoration-blue-500 hover:text-blue-700 break-words"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-6 border-l-2 border-blue-500 bg-neutral-50/70 rounded-r-md px-5 py-3 text-neutral-700 [&>p]:my-2 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0"
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <hr className="my-8 border-0 border-t border-neutral-200" {...props} />
  ),
  code: ({ node, inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code
          className="bg-neutral-100 px-1.5 py-0.5 rounded text-[13px] font-medium text-neutral-800 break-words"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={`block text-[13.5px] leading-[1.6] font-mono text-neutral-800 ${className || ""}`}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ node, ...props }) => (
    <pre
      className="my-5 overflow-x-auto rounded-lg bg-neutral-50 ring-1 ring-neutral-200 p-4 text-[13.5px] leading-[1.6] text-neutral-800"
      {...props}
    />
  ),
  table: ({ node, ...props }) => (
    <div className="my-6 -mx-2 sm:mx-0 overflow-x-auto">
      <table
        className="w-full border-separate border-spacing-0 text-left"
        {...props}
      />
    </div>
  ),
  thead: ({ node, ...props }) => <thead {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => <tr {...props} />,
  th: ({ node, ...props }) => (
    <th
      className="bg-neutral-50 text-left text-[13px] font-semibold text-neutral-700 px-3 py-2 border-b border-neutral-200"
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      className="px-3 py-2 border-b border-neutral-100 text-[14.5px] leading-[1.6] text-neutral-700 align-top"
      {...props}
    />
  ),
  img: ({ node, ...props }) => (
    <img
      className="my-5 rounded-lg max-w-full h-auto"
      loading="lazy"
      {...props}
    />
  ),
};

const timestampToMs = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === "object") {
    if (typeof value.toMillis === "function") {
      try {
        const ms = value.toMillis();
        if (typeof ms === "number" && Number.isFinite(ms)) return ms;
      } catch {
        // Fall through to the seconds/nanoseconds shape below.
      }
    }
    const seconds =
      typeof value._seconds === "number"
        ? value._seconds
        : typeof value.seconds === "number"
          ? value.seconds
          : null;
    if (seconds !== null && Number.isFinite(seconds)) {
      const nanos =
        typeof value._nanoseconds === "number"
          ? value._nanoseconds
          : typeof value.nanoseconds === "number"
            ? value.nanoseconds
            : 0;
      return seconds * 1000 + Math.floor((nanos || 0) / 1e6);
    }
  }
  return null;
};

const formatIssuedDate = (offer) => {
  if (!offer) return null;
  const candidates = [
    offer.publishedAtMs,
    offer.createdAtMs,
    offer.updatedAtMs,
    offer.lastUpdatedMs,
    offer.publishedAt,
    offer.createdAt,
    offer.updatedAt,
    offer.dateCreated,
    offer.lastUpdated,
  ];
  const ms = candidates
    .map(timestampToMs)
    .find((value) => value !== null && value !== undefined);
  if (ms === undefined || ms === null) return null;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const OfferPage = () => {
  const { id } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [gateUnlocked, setGateUnlocked] = useState(
    () => Boolean(getStoredContentSessionId())
  );

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        setError("");
        const apiUrl = getApiBaseUrl();
        const sessionId = gateUnlocked ? getStoredContentSessionId() : null;
        const url = new URL(`${apiUrl}/api/offer/${id}`, window.location.href);
        if (sessionId) {
          url.searchParams.set("sessionId", sessionId);
        }
        const response = await fetch(url.toString());
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || "Offer not found");
        }
        const nextOffer = data?.offer || data;
        setOffer(nextOffer);
        if (nextOffer?.requiresEmailGate) {
          setGateUnlocked(false);
        }
      } catch (err) {
        setError(err.message || "Failed to load offer");
      } finally {
        setLoading(false);
      }
    };
    fetchOffer();
  }, [gateUnlocked, id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f5f1] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-6 h-6 text-neutral-500 animate-spin mx-auto mb-3" />
          <p className="text-neutral-500 text-sm">Loading offer…</p>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-[#f6f5f1] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="text-lg font-semibold text-neutral-900 mb-2">
            Offer unavailable
          </h1>
          <p className="text-neutral-500 text-sm">
            {error || "This offer link may be invalid or expired."}
          </p>
        </div>
      </div>
    );
  }

  if (!gateUnlocked) {
    return (
      <EmailGate
        onAccessGranted={() => setGateUnlocked(true)}
        contentId={`offer-${id}`}
        contentType="offer"
        contentTitle={offer.title || "Asha offer"}
        title="Enter your email to view this offer"
        description={
          <>
            This offer from{" "}
            <span className="text-blue-500 font-semibold">Asha Health</span> is
            prepared for a specific recipient.
          </>
        }
        submitLabel="View offer"
        agreementNoun="this offer"
      />
    );
  }

  const issuedDate = formatIssuedDate(offer);
  const recipient =
    offer.recipientName ||
    offer.leadName ||
    offer.practiceName ||
    null;
  const bodyMarkdown =
    typeof offer.bodyMarkdown === "string" ? offer.bodyMarkdown : "";

  return (
    <div className="min-h-screen bg-[#f6f5f1] py-10 sm:py-16 lg:py-20 px-4 sm:px-6">
      <div className="mx-auto max-w-3xl">
        {/* Letterhead */}
        <header className="flex items-center justify-between mb-6 sm:mb-8 px-1">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-sm">
              <span className="text-white text-xs font-bold">A</span>
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-neutral-900">
                Asha Health
              </div>
              <div className="text-[11px] text-neutral-500 tracking-wide uppercase">
                Special offer
              </div>
            </div>
          </div>
          {issuedDate ? (
            <div className="text-[11px] text-neutral-500 tracking-wide uppercase">
              {issuedDate}
            </div>
          ) : null}
        </header>

        {/* Paper */}
        <article className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04),0_20px_40px_-24px_rgba(15,23,42,0.15)] ring-1 ring-neutral-200/70 overflow-hidden">
          <div className="px-6 sm:px-12 lg:px-16 pt-10 sm:pt-14 lg:pt-16 pb-4">
            <div className="text-[11px] font-medium tracking-[0.14em] uppercase text-blue-600 mb-4">
              Offer prepared{recipient ? ` for ${recipient}` : ""}
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.15] text-neutral-900 tracking-tight">
              {offer.title || "Your special offer from Asha"}
            </h1>
            {offer.subtitle ? (
              <p className="mt-4 text-base sm:text-lg text-neutral-600 leading-relaxed">
                {offer.subtitle}
              </p>
            ) : null}
            <div className="mt-8 h-px bg-neutral-200/80" />
          </div>

          <div className="px-6 sm:px-12 lg:px-16 pb-12 sm:pb-16 lg:pb-20">
            <div className="max-w-none text-[15.5px] leading-[1.75] text-neutral-700 break-words">
              {bodyMarkdown ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {bodyMarkdown}
                </ReactMarkdown>
              ) : (
                <p className="text-neutral-500 italic">
                  This offer has no additional details.
                </p>
              )}
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-1">
          <p className="text-[12px] text-neutral-500 leading-relaxed max-w-xl">
            This offer is prepared for the intended recipient and is not a
            public advertisement. Terms are subject to a signed agreement with
            Asha Health, Inc.
          </p>
          <a
            href="https://asha.dental"
            target="_blank"
            rel="noreferrer"
            className="text-[12px] font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
          >
            asha.dental →
          </a>
        </footer>
      </div>
    </div>
  );
};

export default OfferPage;
